/**
 * RTK Query API Slice for User Management
 * 
 * Handles all user-related endpoints including:
 * - User CRUD operations (list, create, update, delete)
 * - User search and filtering
 * - Bulk operations (import, export)
 * - Profile management
 * - Permission and role management
 * - Organization-specific user operations
 * 
 * Key architectural decisions:
 * - Uses apiClient as baseQuery (maintains snake_case)
 * - Minimal caching for user data (1 minute) for medical compliance
 * - Handles complex pagination structure for user lists
 * - Supports role-based operations (admin endpoints)
 * - Maintains consistent error handling across all endpoints
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import apiClientBaseQuery from './baseQuery';

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: apiClientBaseQuery,
  
  // Medical compliance: No caching for PHI data
  keepUnusedDataFor: 0, // No caching for medical compliance
  refetchOnMountOrArgChange: true,
  
  // Define cache tags for invalidation
  tagTypes: ['User', 'UserList', 'UserPermissions'],
  
  endpoints: (builder) => ({
    // Get paginated list of users - unified endpoint
    getUsers: builder.query({
      query: (params = {}) => ({
        url: '/v1/all-users-pagination',
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          search: params.search,
          org_id: params.client_id, // Pass org_id when filtering by organization
          sort: params.sort,
          order: params.order,
          // Include recordings_count field in the request
          fields: 'id,username,first_name,last_name,client_admin,sys_admin,companyname,clientid,recordings_count,active',
          ...params, // Allow additional filters
        },
      }),
      transformResponse: (response) => {
        // Handle the unified pagination response structure
        // Response format: { success: true, data: { items: [...], pagination: {...} } }
        if (response.data?.data?.items) {
          return {
            users: response.data.data.items,
            pagination: response.data.data.pagination,
          };
        }
        
        // Fallback for unexpected response structures
        return {
          users: response.data || [],
          pagination: response.metadata?.pagination || {
            totalItems: 0,
            currentPage: 1,
            totalPages: 1,
          },
        };
      },
      providesTags: (result) => 
        result?.users
          ? [
              ...result.users.map(({ id }) => ({ type: 'User', id })),
              { type: 'UserList', id: 'LIST' },
            ]
          : [{ type: 'UserList', id: 'LIST' }],
    }),

    // Get single user by ID
    getUserById: builder.query({
      query: (id) => ({
        url: '/v1/users/get-details-by-id',
        method: 'POST',
        data: { user_id: id },
      }),
      transformResponse: (response) => response.data || response,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),

    getUserDetailsById: builder.mutation({
      query: (userId) => ({
        url: '/v1/users/get-details-by-id',
        method: 'POST',
        data: { user_id: userId },
      }),
      transformResponse: (response) => response.data || response,
    }),

    // Create new user
    createUser: builder.mutation({
      query: (userData) => ({
        url: '/v1/users',
        method: 'POST',
        data: userData, // Maintains snake_case from form
      }),
      invalidatesTags: [{ type: 'UserList', id: 'LIST' }],
    }),

    // Register user through admin link (specific registration endpoint)
    registerUser: builder.mutation({
      query: (userData) => ({
        url: '/v1/user-register-through-link',
        method: 'POST',
        data: userData, // Maintains snake_case from form
      }),
      invalidatesTags: [{ type: 'UserList', id: 'LIST' }],
    }),

    // Update user
    updateUser: builder.mutation({
      query: ({ id, ...userData }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        data: userData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        { type: 'UserList', id: 'LIST' },
      ],
    }),

    // Delete user
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'User', id },
        { type: 'UserList', id: 'LIST' },
      ],
    }),

    // Get users by company/organization
    getUsersByCompany: builder.query({
      query: (clientId) => ({
        url: `/users/by-company/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      providesTags: (result) => 
        result
          ? [
              ...result.map(({ id }) => ({ type: 'User', id })),
              { type: 'UserList', id: 'BY_COMPANY' },
            ]
          : [{ type: 'UserList', id: 'BY_COMPANY' }],
    }),

    // Bulk import users
    bulkImportUsers: builder.mutation({
      query: (importData) => ({
        url: '/v1/users/bulk-import',
        method: 'POST',
        data: importData, // Expects: { users: [...], client_id }
      }),
      invalidatesTags: [{ type: 'UserList', id: 'LIST' }],
    }),

    // Create organization admin
    createOrganizationAdmin: builder.mutation({
      query: (adminData) => ({
        url: '/v1/users/organization-admin',
        method: 'POST',
        data: adminData,
      }),
      invalidatesTags: [{ type: 'UserList', id: 'LIST' }],
    }),

    // Get client admins for an organization
    getClientAdmins: builder.query({
      query: (clientId) => ({
        url: `/users/client-admins/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || [],
      providesTags: [{ type: 'UserList', id: 'CLIENT_ADMINS' }],
    }),

    // Update user profile
    updateProfile: builder.mutation({
      query: (profileData) => ({
        url: '/v1/users/update-profile',
        method: 'POST',
        data: profileData, // Expects: { user_id, ...profile_fields }
      }),
      invalidatesTags: (result, error, { user_id }) => [
        { type: 'User', id: user_id },
      ],
    }),

    // Update password (regular password change with old password)
    updatePassword: builder.mutation({
      query: (passwordData) => ({
        url: '/v1/change-password',
        method: 'POST',
        data: passwordData, // Expects: { user_id, old_password, new_password }
      }),
      // No cache invalidation needed for password changes
    }),

    // Forced password reset (no old password required)
    forcedPasswordReset: builder.mutation({
      query: (passwordData) => ({
        url: '/v1/forced-password-reset',
        method: 'POST',
        data: passwordData, // Expects: { user_id, new_password }
      }),
      // No cache invalidation needed for password changes
    }),

    // Get user permissions
    getUserPermissions: builder.query({
      query: (userId) => ({
        url: `/users/permissions/${userId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data || {},
      providesTags: (result, error, userId) => [
        { type: 'UserPermissions', id: userId },
      ],
    }),

    // Toggle user status (active/inactive)
    toggleUserStatus: builder.mutation({
      query: (statusData) => ({
        url: '/v1/users/toggle-status',
        method: 'POST',
        data: statusData, // Expects: { user_id, is_active }
      }),
      invalidatesTags: (result, error, { user_id }) => [
        { type: 'User', id: user_id },
        { type: 'UserList', id: 'LIST' },
      ],
    }),

    // Export users (returns CSV/Excel)
    exportUsers: builder.query({
      query: (params = {}) => ({
        url: '/v1/users/export',
        method: 'GET',
        params: {
          format: params.format || 'csv',
          client_id: params.client_id,
          ...params,
        },
      }),
      // Don't cache export operations
      keepUnusedDataFor: 0,
    }),

    // Send user invite
    sendUserInvite: builder.mutation({
      query: (inviteData) => ({
        url: '/v1/users/send-invite',
        method: 'POST',
        data: inviteData, // Expects: { user_id, email }
      }),
    }),

    // Search users by term (legacy endpoint)
    searchUsersByTerm: builder.query({
      query: (params) => ({
        url: '/v1/get-user-by-term',
        method: 'GET',
        params: {
          client_id: params.client_id,
          s: params.search_term,
        },
      }),
      transformResponse: (response) => response.data || [],
      // Short cache for search results
      keepUnusedDataFor: 30, // 30 seconds
    }),

    // Bulk operations for user management
    bulkUpdateUsers: builder.mutation({
      query: (bulkData) => ({
        url: '/v1/users/bulk-update',
        method: 'POST',
        data: bulkData, // Expects: { user_ids: [...], updates: {...} }
      }),
      invalidatesTags: [{ type: 'UserList', id: 'LIST' }],
    }),

    // Deactivate multiple users
    bulkDeactivateUsers: builder.mutation({
      query: (userIds) => ({
        url: '/v1/users/bulk-deactivate',
        method: 'POST',
        data: { user_ids: userIds },
      }),
      invalidatesTags: [{ type: 'UserList', id: 'LIST' }],
    }),

    // Get user activity log
    getUserActivity: builder.query({
      query: ({ userId, ...params }) => ({
        url: `/users/${userId}/activity`,
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 50,
          ...params,
        },
      }),
      transformResponse: (response) => response.data || [],
      // Don't cache activity logs (audit trail)
      keepUnusedDataFor: 0,
    }),

    // Get current user's profile
    getMyProfile: builder.query({
      query: ({ clientid, userid }) => ({
        url: '/v1/get-my-profile',
        method: 'POST',
        data: { clientid, userid },
      }),
      transformResponse: (response) => response.data || {},
      providesTags: (result, error, { userid }) => [
        { type: 'User', id: userid },
      ],
    }),

    // Update current user's profile
    updateMyProfile: builder.mutation({
      query: (profileData) => ({
        url: '/v1/update-my-profile',
        method: 'POST',
        data: profileData,
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

// Export hooks for use in components
export const {
  // Queries
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetUserDetailsByIdMutation,
  useGetUsersByCompanyQuery,
  useGetClientAdminsQuery,
  useGetUserPermissionsQuery,
  useExportUsersQuery,
  useSearchUsersByTermQuery,
  useGetUserActivityQuery,
  useGetMyProfileQuery,
  
  // Mutations
  useCreateUserMutation,
  useRegisterUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useBulkImportUsersMutation,
  useCreateOrganizationAdminMutation,
  useUpdateProfileMutation,
  useUpdatePasswordMutation,
  useForcedPasswordResetMutation,
  useToggleUserStatusMutation,
  useSendUserInviteMutation,
  useBulkUpdateUsersMutation,
  useBulkDeactivateUsersMutation,
  useUpdateMyProfileMutation,
  
  // Lazy queries (for manual triggering)
  useLazyGetUsersQuery,
  useLazyExportUsersQuery,
  useLazySearchUsersByTermQuery,
  
  // For manual cache invalidation if needed
  util: { invalidateTags },
} = userApi;

export default userApi;
